import { useEffect, useState } from 'react';

import { Query } from '@actual-app/core/shared/query';

import { aqlQuery } from '#queries/aqlQuery';

// Cabecera del buscador con cifras EXACTAS: la lista carga de 50 en 50, pero
// «23 movimientos · −443,45 €» debe contar todas las coincidencias. Se piden
// al servidor con consultas agregadas (count + sum) con los mismos filtros que
// la lista, sin ordenar ni paginar.
//
// Divididos, igual que `resumenBusqueda` con lo cargado: el padre ya lleva el
// importe total, así que una parte solo cuenta si su padre no coincide.
//   1. Movimientos que no son partes (normales y padres): count + sum.
//   2. Partes que coinciden, agrupadas por padre: count + sum por padre.
//   3. De esos padres, cuáles coinciden también (sus partes no se suman).
// Con `splits: 'inline'` el servidor ya quita los padres, y las partes cuentan
// todas. Los pagos fijos previstos no salen de la base de datos: no cuentan.
// No hay filtro de fecha: lo apuntado para días futuros cuenta, como en la
// lista.

export type ResumenExacto = { cantidad: number; total: number };

type FilaAgregada = { count?: number | null; total?: number | null };
type GrupoPartes = FilaAgregada & { parent_id: string | null };

const AGREGADOS = [
  { count: { $count: '$id' } },
  { total: { $sum: '$amount' } },
];

/** La consulta de la lista sin orden, página ni columnas. */
export function consultaBase(query: Query): Query {
  return new Query({
    ...query.state,
    selectExpressions: [],
    orderExpressions: [],
    groupExpressions: [],
    calculation: false,
    limit: null,
    offset: null,
  });
}

export function consultaSinPartes(query: Query): Query {
  return consultaBase(query).filter({ is_child: false }).select(AGREGADOS);
}

export function consultaPartesPorPadre(query: Query): Query {
  return consultaBase(query)
    .filter({ is_child: true })
    .groupBy(['parent_id'])
    .select(['parent_id', ...AGREGADOS]);
}

export function consultaPadresQueCoinciden(
  query: Query,
  padres: readonly string[],
): Query {
  return consultaBase(query)
    .filter({ is_parent: true, id: { $oneof: [...padres] } })
    .select(['id']);
}

/** Junta las tres consultas sin contar padre y partes dos veces. */
export function combinarResumen(
  sinPartes: FilaAgregada | undefined,
  partesPorPadre: readonly GrupoPartes[],
  padresQueCoinciden: ReadonlySet<string>,
): ResumenExacto {
  let cantidad = sinPartes?.count ?? 0;
  let total = sinPartes?.total ?? 0;
  for (const grupo of partesPorPadre) {
    if (grupo.parent_id && padresQueCoinciden.has(grupo.parent_id)) continue;
    cantidad += grupo.count ?? 0;
    total += grupo.total ?? 0;
  }
  return { cantidad, total };
}

export async function calcularResumenExacto(
  query: Query,
): Promise<ResumenExacto> {
  const [{ data: sinPartes }, { data: partes }] = await Promise.all([
    aqlQuery(consultaSinPartes(query)),
    aqlQuery(consultaPartesPorPadre(query)),
  ]);
  const grupos = (partes ?? []) as GrupoPartes[];
  const padres = grupos
    .map(g => g.parent_id)
    .filter((id): id is string => !!id);
  let coinciden = new Set<string>();
  if (padres.length > 0) {
    const { data } = await aqlQuery(consultaPadresQueCoinciden(query, padres));
    coinciden = new Set(((data ?? []) as { id: string }[]).map(f => f.id));
  }
  return combinarResumen(
    ((sinPartes ?? []) as FilaAgregada[])[0],
    grupos,
    coinciden,
  );
}

/**
 * Cifras exactas de la búsqueda, o `null` mientras se calculan (entonces se
 * enseña lo cargado). Se recalcula al cambiar la consulta y cada vez que la
 * lista se recarga (`version`, p. ej. el array de movimientos), conservando
 * la cifra anterior de la misma consulta mientras tanto.
 */
export function useResumenBusquedaExacto(
  query: Query | undefined,
  activo: boolean,
  version?: unknown,
): ResumenExacto | null {
  const clave = activo && query ? query.serializeAsString() : null;
  const [estado, setEstado] = useState<{
    clave: string;
    resumen: ResumenExacto;
  } | null>(null);

  useEffect(() => {
    if (!clave || !query) return;
    let vigente = true;
    calcularResumenExacto(query).then(
      resumen => {
        if (vigente) setEstado({ clave, resumen });
      },
      () => {
        // Sin cifra exacta: se queda lo cargado.
      },
    );
    return () => {
      vigente = false;
    };
    // `query` cambia con `clave`; `version` fuerza el recálculo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, version]);

  return estado && estado.clave === clave ? estado.resumen : null;
}
